import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchSingleNews } from "../api/newsApi";
import { NEWS_CONFIG } from "../config/newsConfig";
import {
  buildInternalNewsUrl,
  getSharedNewsIdFromLocation,
  getSiteHomePath,
} from "../utils/appPath";

const copyTextToClipboard = async (text) => {
  /*
  |------------------------------------------------------------------------
  | Always copy the internal news link directly.
  |
  | The Web Share API is intentionally not used here. The button must have
  | identical behavior on desktop, Android, iOS, PWA and native wrappers:
  | pressing it copies the link and never opens the operating-system share
  | sheet.
  |------------------------------------------------------------------------
  */
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Some browsers expose Clipboard API but reject it because of context
      // or permission rules. Continue with the user-gesture fallback below.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.setAttribute("aria-hidden", "true");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";

  document.body.appendChild(textarea);

  try {
    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    const didCopy = document.execCommand("copy");

    if (!didCopy) {
      throw new Error("Unable to copy the news link");
    }
  } finally {
    textarea.remove();
  }
};

const getNewsPoolKey = (item) => {
  const id = item?.id;
  if (id !== undefined && id !== null) return `id:${id}`;
  return item?.link || "";
};

const mergeUniqueNewsPool = (items) => {
  const seen = new Set();
  const unique = [];

  items.forEach((item) => {
    const key = getNewsPoolKey(item);
    if (!key || seen.has(key)) return;

    seen.add(key);
    unique.push(item);
  });

  return unique;
};

export const useSharedNews = ({
  newsPool = [],
  loading = false,
  contentScrollParent,
  mainScrollParent,
} = {}) => {
  const [sharedNewsId, setSharedNewsId] = useState(() => getSharedNewsIdFromLocation());
  const [sharedNewsFromApi, setSharedNewsFromApi] = useState(null);
  const [sharedNewsError, setSharedNewsError] = useState("");
  const [sharedNewsRetrySignal, setSharedNewsRetrySignal] = useState(0);
  const [sharedNewsSuppressViewIncrement, setSharedNewsSuppressViewIncrement] =
    useState(false);
  const sharedNewsScrollSnapshotRef = useRef(null);

  const combinedNews = useMemo(() => {
    return mergeUniqueNewsPool([...newsPool, sharedNewsFromApi]);
  }, [newsPool, sharedNewsFromApi]);

  const sharedNews = useMemo(() => {
    if (!sharedNewsId) return null;

    return (
      combinedNews.find((item) => String(item?.id) === String(sharedNewsId)) ||
      null
    );
  }, [combinedNews, sharedNewsId]);

  /*
  |------------------------------------------------------------------------
  | Shared-news errors are surfaced by the top ticker, never a blocking modal.
  |------------------------------------------------------------------------
  */
  const shouldShowSharedNewsModal = Boolean(sharedNews);
  const showSharedNewsLoadingTicker =
    Boolean(sharedNewsId) && !sharedNews && !sharedNewsError;

  const closeSharedNews = useCallback(() => {
    setSharedNewsId(null);
    setSharedNewsError("");
    setSharedNewsFromApi(null);
    setSharedNewsSuppressViewIncrement(false);

    sharedNewsScrollSnapshotRef.current = null;

    const homePath = getSiteHomePath();

    /*
    |------------------------------------------------------------------------
    | A news preview is transient UI, not browser navigation.
    |
    | Keep the address bar synchronized with the app while closing the modal,
    | but replace the current history entry instead of moving backward or
    | creating another entry. This keeps Chrome/Safari Back and Forward from
    | treating an opened-and-closed preview as navigation.
    |------------------------------------------------------------------------
    */
    window.history.replaceState(window.history.state, "", homePath);
  }, []);

  const handleShareNews = useCallback(async (news) => {
    const shareUrl = buildInternalNewsUrl(news);

    await copyTextToClipboard(shareUrl);

    return shareUrl;
  }, []);

  const handleViewNews = useCallback(
    (news, viewContext = {}) => {
      if (!news?.id) return;

      const shareUrl = buildInternalNewsUrl(news);

      const snapshot = {
        windowX: viewContext.windowX ?? window.scrollX,
        windowY: viewContext.windowY ?? window.scrollY,
        contentTop: contentScrollParent?.scrollTop ?? null,
        mainTop: mainScrollParent?.scrollTop ?? null,
        activeScroller: viewContext.scrollContainer || null,
        activeScrollerTop: viewContext.scrollTop ?? null,
      };

      sharedNewsScrollSnapshotRef.current = snapshot;

      setSharedNewsId(String(news.id));
      setSharedNewsFromApi(news);
      setSharedNewsError("");
      setSharedNewsSuppressViewIncrement(
        Boolean(viewContext.suppressViewIncrement)
      );

      /*
      |----------------------------------------------------------------------
      | Opening a preview must not add a browser-history entry.
      |
      | We still expose /news/:id in the address bar, but replace the current
      | URL in place. Closing the preview replaces it with Home again, leaving
      | the browser's Back/Forward stack exactly as it was before the preview.
      |----------------------------------------------------------------------
      */
      window.history.replaceState(
        window.history.state,
        "",
        new URL(shareUrl).pathname
      );

      requestAnimationFrame(() => {
        if (contentScrollParent && snapshot.contentTop !== null) {
          contentScrollParent.scrollTop = snapshot.contentTop;
        }

        if (mainScrollParent && snapshot.mainTop !== null) {
          mainScrollParent.scrollTop = snapshot.mainTop;
        }

        if (snapshot.activeScroller && snapshot.activeScrollerTop !== null) {
          snapshot.activeScroller.scrollTop = snapshot.activeScrollerTop;
        }

        window.scrollTo(snapshot.windowX, snapshot.windowY);
      });
    },
    [contentScrollParent, mainScrollParent]
  );

  useEffect(() => {
    const handlePopState = () => {
      setSharedNewsSuppressViewIncrement(false);
      setSharedNewsId(getSharedNewsIdFromLocation());
    };

    window.addEventListener("popstate", handlePopState);

    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("shared-news-open", shouldShowSharedNewsModal);
    document.documentElement.classList.toggle(
      "shared-news-open",
      shouldShowSharedNewsModal
    );

    if (!shouldShowSharedNewsModal) {
      sharedNewsScrollSnapshotRef.current = null;

      return () => {
        document.body.classList.remove("shared-news-open");
        document.documentElement.classList.remove("shared-news-open");
      };
    }

    const snapshot = sharedNewsScrollSnapshotRef.current || {
      windowX: window.scrollX,
      windowY: window.scrollY,
      contentTop: contentScrollParent?.scrollTop ?? null,
      mainTop: mainScrollParent?.scrollTop ?? null,
    };

    sharedNewsScrollSnapshotRef.current = snapshot;

    const restoreScrollPosition = () => {
      if (!snapshot) return;

      if (contentScrollParent && snapshot.contentTop !== null) {
        contentScrollParent.scrollTop = snapshot.contentTop;
      }

      if (mainScrollParent && snapshot.mainTop !== null) {
        mainScrollParent.scrollTop = snapshot.mainTop;
      }

      if (snapshot.activeScroller && snapshot.activeScrollerTop !== null) {
        snapshot.activeScroller.scrollTop = snapshot.activeScrollerTop;
      }

      window.scrollTo(snapshot.windowX, snapshot.windowY);
    };

    let restoreFrame = 0;
    let restoreCount = 0;

    const keepRestoringScroll = () => {
      restoreScrollPosition();
      restoreCount += 1;

      if (restoreCount < 8) {
        restoreFrame = requestAnimationFrame(keepRestoringScroll);
      }
    };

    restoreFrame = requestAnimationFrame(keepRestoringScroll);

    let lastTouchY = 0;

    const trackTouchStart = (event) => {
      lastTouchY = event.touches?.[0]?.clientY ?? 0;
    };

    const preventBackgroundTouchScroll = (event) => {
      const content = event.target?.closest?.(".shared-news-modal__content");

      if (!content) {
        event.preventDefault();
        return;
      }

      const currentTouchY = event.touches?.[0]?.clientY ?? lastTouchY;
      const deltaY = currentTouchY - lastTouchY;
      lastTouchY = currentTouchY;

      const atTop = content.scrollTop <= 0;
      const atBottom =
        content.scrollTop + content.clientHeight >= content.scrollHeight - 1;
      const cannotScroll = content.scrollHeight <= content.clientHeight;

      if (cannotScroll || (atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
        event.preventDefault();
      }
    };

    const preventBackgroundWheelScroll = (event) => {
      if (event.target?.closest?.(".shared-news-modal__content")) return;
      event.preventDefault();
    };

    document.addEventListener("touchstart", trackTouchStart, { passive: false });
    document.addEventListener("touchmove", preventBackgroundTouchScroll, {
      passive: false,
    });
    document.addEventListener("wheel", preventBackgroundWheelScroll, {
      passive: false,
    });

    return () => {
      document.body.classList.remove("shared-news-open");
      document.documentElement.classList.remove("shared-news-open");

      if (restoreFrame) cancelAnimationFrame(restoreFrame);

      document.removeEventListener("touchstart", trackTouchStart);
      document.removeEventListener("touchmove", preventBackgroundTouchScroll);
      document.removeEventListener("wheel", preventBackgroundWheelScroll);
    };
  }, [contentScrollParent, mainScrollParent, shouldShowSharedNewsModal]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleConnectionRestored = () => {
      if (!sharedNewsId || sharedNews || !sharedNewsError) return;

      setSharedNewsRetrySignal((current) => current + 1);
    };

    window.addEventListener(
      NEWS_CONFIG.connectionRestoredEventName,
      handleConnectionRestored
    );

    return () => {
      window.removeEventListener(
        NEWS_CONFIG.connectionRestoredEventName,
        handleConnectionRestored
      );
    };
  }, [sharedNews, sharedNewsError, sharedNewsId]);

  useEffect(() => {
    if (!sharedNewsId || sharedNews || loading) return;

    const controller = new AbortController();

    const fetchSharedNews = async () => {
      setSharedNewsError("");

      try {
        const nextItem = await fetchSingleNews({
          id: sharedNewsId,
          signal: controller.signal,
        });

        setSharedNewsFromApi(nextItem);
      } catch (error) {
        if (error.name === "AbortError") return;

        setSharedNewsError(error.message || "خبر پیدا نشد");
      }
    };

    fetchSharedNews();

    return () => controller.abort();
  }, [loading, sharedNews, sharedNewsId, sharedNewsRetrySignal]);

  return {
    sharedNewsId,
    sharedNews,
    sharedNewsError,
    shouldShowSharedNewsModal,
    showSharedNewsLoadingTicker,
    sharedNewsSuppressViewIncrement,
    closeSharedNews,
    handleShareNews,
    handleViewNews,
  };
};
